import React, { useState, useEffect } from 'react';
import { getLogs, clearLogs } from '../../api';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import Modal from '../../components/Modal';
import {
  ClipboardDocumentListIcon,
  UserPlusIcon,
  TrashIcon,
  PencilSquareIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const ACTION_CONFIG = {
  CREATE_USER: { icon: UserPlusIcon, color: 'var(--success)', label: 'Created User' },
  DELETE_USER: { icon: TrashIcon, color: 'var(--danger)', label: 'Deleted User' },
  EDIT_USER: { icon: PencilSquareIcon, color: 'var(--info)', label: 'Edited User' },
  CHANGE_ROLE: { icon: ShieldCheckIcon, color: 'var(--warning)', label: 'Changed Role' },
  CREATE_ORG: { icon: BuildingOfficeIcon, color: 'var(--success)', label: 'Created Org' },
  DELETE_ORG: { icon: TrashIcon, color: 'var(--danger)', label: 'Deleted Org' },
  ADD_MEMBER: { icon: UserGroupIcon, color: 'var(--info)', label: 'Added Member' },
  REMOVE_MEMBER: { icon: UserGroupIcon, color: 'var(--warning)', label: 'Removed Member' },
};

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const res = await getLogs();
      setLogs(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    try {
      await clearLogs();
      setLogs([]);
      setShowClearConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear logs');
      setShowClearConfirm(false);
    }
  };

  const filtered = logs.filter(log => {
    const matchSearch = !search || log.username?.toLowerCase().includes(search.toLowerCase()) || log.details?.toLowerCase().includes(search.toLowerCase());
    const matchAction = !filterAction || log.action === filterAction;
    return matchSearch && matchAction;
  });

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Activity Logs</h2>
          <div className="subtitle">Track all system actions</div>
        </div>
        <button className="btn btn-danger" onClick={() => setShowClearConfirm(true)} disabled={logs.length === 0}>
          <TrashIcon className="icon" /> Clear Logs
        </button>
      </div>

      <Modal isOpen={showClearConfirm} onClose={() => setShowClearConfirm(false)} title="Clear All Logs">
        <p>Are you sure you want to delete all activity logs? This action cannot be undone.</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={() => setShowClearConfirm(false)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleClearLogs}>Clear All</button>
        </div>
      </Modal>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <MagnifyingGlassIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: 'var(--text-muted)' }} />
          <input
            className="form-input"
            placeholder="Search logs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>
        <select className="form-input" value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">All Actions</option>
          {Object.keys(ACTION_CONFIG).map(key => (
            <option key={key} value={key}>{ACTION_CONFIG[key].label}</option>
          ))}
        </select>
      </div>

      <div className="members-list">
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <ClipboardDocumentListIcon style={{ width: 48, height: 48, opacity: 0.5 }} />
            <p style={{ marginTop: 12 }}>No logs found</p>
          </div>
        )}
        {filtered.map(log => {
          const config = ACTION_CONFIG[log.action] || { icon: ClipboardDocumentListIcon, color: 'var(--text-muted)', label: log.action };
          const Icon = config.icon;
          return (
            <div key={log._id} className="member-item" style={{ padding: '12px 16px' }}>
              <div className="member-info" style={{ gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: config.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon style={{ width: 18, height: 18, color: config.color }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{log.details}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    by <strong>@{log.username}</strong> • {new Date(log.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
              <span className="badge" style={{ background: config.color + '22', color: config.color, fontSize: 11 }}>
                {config.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
